import React, { useState, useRef, useImperativeHandle, useEffect } from 'react'
import YnsIcon from '../icons/YnsIcon'

const QuestionWithChoices = React.forwardRef(
  (
    { question, choices, onRatingChange, onTabOut, disabled, className },
    ref,
  ) => {
    const [hoverRating, setHoverRating] = useState(null)
    const [focusRating, setFocusRating] = useState(null)
    const [feedbackRating, setFeedbackRating] = useState(null)
    const [focusedIndex, setFocusedIndex] = useState(0)
    const buttonRefs = useRef([])

    useImperativeHandle(ref, () => ({
      focusFirst: () => {
        setFocusedIndex(0)
        buttonRefs.current[0]?.focus()
      },
    }))

    useEffect(() => {
      if (disabled) setFeedbackRating(null)
    }, [disabled])

    const handleRatingClick = (rating, index) => {
      if (disabled) return
      const newRating = feedbackRating === rating ? null : rating
      setFeedbackRating(newRating)
      setFocusedIndex(index)
      onRatingChange(newRating)
    }

    const handleKeyDown = (e, index) => {
      let nextIndex = null
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        nextIndex = Math.min(index + 1, choices.length - 1)
        e.preventDefault()
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        nextIndex = Math.max(index - 1, 0)
        e.preventDefault()
      } else if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault()
        e.stopPropagation()
        onTabOut?.()
      }
      if (nextIndex !== null) {
        setFocusedIndex(nextIndex)
        buttonRefs.current[nextIndex]?.focus()
      }
    }

    const handleRatingMouseOver = (rating) => setHoverRating(rating)
    const handleRatingMouseOut = () => setHoverRating(null)

    const gradientColors = [
      '#FFECB3',
      '#FFD54F',
      '#FFCA28',
      '#FFC000',
      '#FFB72F',
    ]

    return (
      <div className={className || 'mb-10'}>
        <p className="!mb-5 !font-bold">{question}</p>
        <div
          role="radiogroup"
          aria-label={question}
          className={`grid max-w-[90%] items-center md:max-w-[400px] ${choices.length === 4 ? 'grid-cols-4' : 'grid-cols-5'}`}
        >
          {choices.map((choice, index) => {
            const ratingValue = index + 1
            const isSolid =
              hoverRating !== null
                ? ratingValue <= hoverRating
                : feedbackRating !== null && ratingValue <= feedbackRating

            const starColor = isSolid
              ? gradientColors[index] || '#767676'
              : '#767676'

            const showTooltip =
              (hoverRating !== null && hoverRating === ratingValue) ||
              (focusRating !== null && focusRating === ratingValue) ||
              (hoverRating === null &&
                focusRating === null &&
                feedbackRating === ratingValue)

            return (
              <button
                key={index}
                ref={(el) => (buttonRefs.current[index] = el)}
                type="button"
                role="radio"
                aria-checked={feedbackRating === ratingValue}
                tabIndex={index === focusedIndex ? 0 : -1}
                aria-label={choice}
                disabled={disabled}
                className={`rating ga-trackable relative flex flex-col items-center justify-center border-0 bg-transparent p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-intoo-blue-medium-700 focus-visible:ring-offset-2 ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
                onClick={() => handleRatingClick(ratingValue, index)}
                onMouseOver={() =>
                  !disabled && handleRatingMouseOver(ratingValue)
                }
                onMouseOut={() => !disabled && handleRatingMouseOut()}
                onFocus={() => !disabled && setFocusRating(ratingValue)}
                onBlur={() => !disabled && setFocusRating(null)}
                onKeyDown={(e) => !disabled && handleKeyDown(e, index)}
              >
                <YnsIcon
                  name={isSolid ? 'star-solid' : 'star'}
                  size={32}
                  style={{ color: starColor }}
                  aria-hidden="true"
                />
                {showTooltip && (
                  <span
                    className="absolute top-full mt-2 whitespace-nowrap rounded-md border bg-white px-2 py-1 text-center text-sm shadow-md transition-opacity duration-200 ease-in-out"
                    style={{
                      minWidth: '100px',
                      opacity: 1,
                      pointerEvents: 'auto',
                    }}
                  >
                    {choice}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    )
  },
)

QuestionWithChoices.displayName = 'QuestionWithChoices'

export default QuestionWithChoices
